import { useState } from "react";
import { tr } from "@/utils/i18n";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Mail, User, Phone, Sparkles, Shield, ArrowRight, CheckCircle } from "lucide-react";
import api from "@/utils/api";

// Common country codes
const COUNTRY_CODES = [
  { code: "+1", country: "US/CA", flag: "🇺🇸" },
  { code: "+44", country: "UK", flag: "🇬🇧" },
  { code: "+55", country: "BR", flag: "🇧🇷" },
  { code: "+34", country: "ES", flag: "🇪🇸" },
  { code: "+351", country: "PT", flag: "🇵🇹" },
  { code: "+49", country: "DE", flag: "🇩🇪" },
  { code: "+33", country: "FR", flag: "🇫🇷" },
  { code: "+39", country: "IT", flag: "🇮🇹" },
  { code: "+31", country: "NL", flag: "🇳🇱" },
  { code: "+61", country: "AU", flag: "🇦🇺" },
  { code: "+81", country: "JP", flag: "🇯🇵" },
  { code: "+86", country: "CN", flag: "🇨🇳" },
  { code: "+91", country: "IN", flag: "🇮🇳" },
  { code: "+52", country: "MX", flag: "🇲🇽" },
  { code: "+54", country: "AR", flag: "🇦🇷" },
  { code: "+56", country: "CL", flag: "🇨🇱" },
  { code: "+57", country: "CO", flag: "🇨🇴" },
  { code: "+58", country: "VE", flag: "🇻🇪" },
  { code: "+27", country: "ZA", flag: "🇿🇦" },
  { code: "+971", country: "AE", flag: "🇦🇪" },
  { code: "+966", country: "SA", flag: "🇸🇦" },
  { code: "+7", country: "RU", flag: "🇷🇺" },
  { code: "+82", country: "KR", flag: "🇰🇷" },
  { code: "+65", country: "SG", flag: "🇸🇬" },
  { code: "+60", country: "MY", flag: "🇲🇾" },
  { code: "+63", country: "PH", flag: "🇵🇭" },
  { code: "+66", country: "TH", flag: "🇹🇭" },
  { code: "+84", country: "VN", flag: "🇻🇳" },
  { code: "+62", country: "ID", flag: "🇮🇩" },
  { code: "+48", country: "PL", flag: "🇵🇱" },
  { code: "+46", country: "SE", flag: "🇸🇪" },
  { code: "+47", country: "NO", flag: "🇳🇴" },
  { code: "+45", country: "DK", flag: "🇩🇰" },
  { code: "+358", country: "FI", flag: "🇫🇮" },
  { code: "+353", country: "IE", flag: "🇮🇪" },
  { code: "+41", country: "CH", flag: "🇨🇭" },
  { code: "+43", country: "AT", flag: "🇦🇹" },
  { code: "+32", country: "BE", flag: "🇧🇪" },
  { code: "+30", country: "GR", flag: "🇬🇷" },
  { code: "+90", country: "TR", flag: "🇹🇷" },
  { code: "+20", country: "EG", flag: "🇪🇬" },
  { code: "+234", country: "NG", flag: "🇳🇬" },
  { code: "+254", country: "KE", flag: "🇰🇪" },
  { code: "+972", country: "IL", flag: "🇮🇱" },
  { code: "+64", country: "NZ", flag: "🇳🇿" },
];

export default function LeadCaptureGate() {
  const navigate = useNavigate();
  const { setUserFromGate } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    country_code: "+1"
  });

  const handleGoogleLogin = () => {
    // Use window.location.origin to support all environments (preview, production, custom domains)
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLeadCapture = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error(tr(language, "Please enter your name", "Por favor, insira seu nome", "Por favor ingresa tu nombre"));
      return;
    }
    if (!formData.email.trim()) {
      toast.error(tr(language, "Please enter your email", "Por favor, insira seu email", "Por favor ingresa tu email"));
      return;
    }
    if (!formData.phone.trim()) {
      toast.error(tr(language, "Please enter your phone number", "Por favor, insira seu telefone", "Por favor ingresa tu teléfono"));
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.post('/auth/lead-capture', {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        country_code: formData.country_code,
        ref: localStorage.getItem('referral_code') || undefined,
      });
      localStorage.removeItem('referral_code');
      
      if (response.data.access_token) {
        // Token is kept in memory via setUserFromGate — not localStorage.
        // The httpOnly cookie set by the backend handles persistence.
        localStorage.setItem('user_country_code', formData.country_code);
        
        const userData = response.data.user || {
          email: formData.email.trim().toLowerCase(),
          name: formData.name.trim()
        };
        
        toast.success(response.data.message || "Welcome!");
        setUserFromGate(userData, response.data.access_token);
        
        // Reliable fallback: if gate doesn't close in 1.5s, force navigate
        setTimeout(() => {
          const gateStillVisible = document.querySelector('[data-testid="lead-submit"]');
          if (gateStillVisible) {
            window.location.href = "/";
          }
        }, 1500);
      } else {
        toast.error(
          tr(language, "Login failed. Please try again.", "Erro ao fazer login. Tente novamente.", "Error al iniciar sesión. Inténtalo de nuevo.")
        );
      }
    } catch (error) {
      let message;
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        message = tr(language, "Request timed out. Check your connection and try again.", "Tempo esgotado. Verifique sua conexão e tente novamente.", "Tiempo agotado. Verifica tu conexión e inténtalo de nuevo.");
      } else if (!error.response) {
        message = tr(language, "Connection error. Check your internet and try again.", "Erro de conexão. Verifique sua internet e tente novamente.", "Error de conexión. Verifica tu internet e inténtalo de nuevo.");
      } else {
        message = tr(language, "Failed to submit. Please try again.", "Erro ao enviar. Tente novamente.", "Error al enviar. Inténtalo de nuevo.");
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#050505]/95 backdrop-blur-sm p-4">
      {/* Language Switcher - Top Right */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      
      <Card className="w-full max-w-md bg-zinc-950 border-zinc-800 shadow-2xl shadow-cyan-500/10">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 text-cyan-400" />
          </div>
          <CardTitle className="text-2xl text-white">
            {t("getStartedFree")}
          </CardTitle>
          <p className="text-zinc-400 text-sm mt-2">
            {t("createAccount")}
          </p>
        </CardHeader>
        
        <CardContent className="pt-4">
          {/* Google Login Button */}
          <Button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full h-12 bg-white hover:bg-gray-100 text-gray-800 font-medium mb-4"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t("continueWithGoogle")}
          </Button>

          <div className="relative my-6">
            <Separator className="bg-zinc-800" />
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-zinc-950 px-4 text-sm text-zinc-500">
              {t("orUseEmail")}
            </span>
          </div>

          {/* Lead Capture Form */}
          <form onSubmit={handleLeadCapture} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">{t("fullName")} *</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                <Input
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-10 bg-zinc-900 border-zinc-800 focus:border-cyan-500"
                  required
                  data-testid="lead-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">{t("emailAddress")} *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 bg-zinc-900 border-zinc-800 focus:border-cyan-500"
                  required
                  data-testid="lead-email"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">{t("phoneNumber")} *</Label>
              <div className="flex gap-2">
                {/* Country Code Selector */}
                <Select 
                  value={formData.country_code} 
                  onValueChange={(value) => setFormData({ ...formData, country_code: value })}
                >
                  <SelectTrigger className="w-[100px] bg-zinc-900 border-zinc-800">
                    <SelectValue placeholder="+1" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 max-h-[300px]">
                    {COUNTRY_CODES.map((item) => (
                      <SelectItem 
                        key={item.code} 
                        value={item.code}
                        className="text-zinc-300 hover:text-white"
                      >
                        <span className="flex items-center gap-2">
                          <span>{item.flag}</span>
                          <span>{item.code}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Phone Number Input */}
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                  <Input
                    type="tel"
                    placeholder="555 123 4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="pl-10 bg-zinc-900 border-zinc-800 focus:border-cyan-500"
                    required
                    data-testid="lead-phone"
                  />
                </div>
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full h-12 btn-primary font-bold"
              disabled={loading}
              data-testid="lead-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="spinner w-4 h-4" />
                  {tr(language, 'Processing...', 'Processando...', 'Procesando...')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {t("getAccess")}
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          {/* Trust indicators */}
          <div className="mt-6 pt-4 border-t border-zinc-800">
            <div className="flex items-center justify-center gap-6 text-xs text-zinc-500">
              <span className="flex items-center gap-1">
                <Shield className="w-3 h-3" />
                {t("secure")}
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                {t("freeToUse")}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
